import {
  compact,
  defaults,
  filter,
  flow,
  get,
  includes,
  isPlainObject,
  isUndefined,
  lowerCase,
  map,
  partial,
  pick,
  some,
  sortBy,
} from 'lodash/fp';
import getParentProductId from '@ahmdigital/logic/lib/utils/get-parent-product-id';

import { ProductType } from '../../types/product';
import combineProductNames from '../../utils/combine-product-names';
import constants from '../../ahm-constants';
import getSaleableProductIdsKeyedByType from '../../utils/get-saleable-product-ids-keyed-by-type';
import productData from './data';

type Options = { scale?: string; state?: string };
type ProductApi = { get: (endpoint: string) => Promise<{ product: ProductType }> };

const ProductService = {
  content: function content(productId: ProductType['id'], path: string) {
    // Look to see if a productId has a parentProductId.
    const parentProductId = getParentProductId(productId);

    // Lookup this product’s content and find the path value from the content object
    return get(`${parentProductId || productId}.${path}`, productData) || null;
  },
  filterAvailableProducts: function filterAvailableProducts(products: ProductType[], options: Options = {}) {
    const { scale, state } = options;

    const filteredProducts = filter((product) => {
      const stateOk = isUndefined(state) || (state && ProductService.isAvailableIn(product, state));
      const scaleOk = isUndefined(scale) || (scale && ProductService.isAvailableFor(product, scale));
      return stateOk && scaleOk;
    }, products);

    return filteredProducts;
  },
  getCartObject: flow(
    pick(['hospitalTier', 'id', 'name', 'rank', 'scales', 'type']),
    defaults({
      id: null,
      name: null,
      rank: null,
      scales: null,
      type: null,
    }),
  ),

  getCombinedCartObject: ({ hospital, extras }: { hospital: ProductType; extras: ProductType }) => ({
    hospitalTier: hospital.hospitalTier,
    id: `${hospital.id}-${extras.id}`,
    name: combineProductNames(hospital, extras),
    scales: hospital.scales, // set to hospital scales as hospital products are not always supported for all scales
    type: constants.PRODUCT_TYPE.COMBINED,
  }),
  getCombinedId: (product1: ProductType, product2: ProductType) => {
    const { EXTRAS, HOSPITAL } = constants.PRODUCT_TYPE;
    if (!includes(product1?.type, [EXTRAS, HOSPITAL])) {
      throw new Error('product1 must be a hospital or extras product');
    }
    if (!includes(product2?.type, [EXTRAS, HOSPITAL])) {
      throw new Error('product2 must be a hospital or extras product');
    }
    if (product1.type === product2.type) {
      throw new Error(`Products should not be of the same type: '${product1.type}'`);
    }
    const hospital = product1.type === HOSPITAL ? product1 : product2;
    const extras = product1.type === EXTRAS ? product1 : product2;
    return `${hospital.id}-${extras.id}`;
  },
  getComplementaryProductIds: function getComplementaryProductIds(product: ProductType) {
    if (!isPlainObject(product)) {
      throw new Error(`Product should be an object. Product passed: ${product}`);
    }
    const complementaryType = ProductService.getComplementaryType(product);
    // @ts-expect-error - Automatic, Please fix when editing this file
    return getSaleableProductIdsKeyedByType()[complementaryType];
  },
  getComplementaryProducts: (product: ProductType, allProducts: ProductType[]) => {
    const complementaryProductIds = ProductService.getComplementaryProductIds(product);

    const getProduct = partial(ProductService.getProduct, [allProducts]);

    const complementaryProducts = flow(
      map(getProduct),
      compact, // remove any products that haven’t loaded yet.
      sortBy('rank'),
    )(complementaryProductIds);
    return complementaryProducts;
  },
  getComplementaryType: function getComplementaryType(product: ProductType) {
    if (product.type === constants.PRODUCT_TYPE.HOSPITAL) {
      return constants.PRODUCT_TYPE.EXTRAS;
    }

    if (product.type === constants.PRODUCT_TYPE.EXTRAS) {
      return constants.PRODUCT_TYPE.HOSPITAL;
    }

    return null;
  },

  /**
   * Builds a sentence to describe the daily charge costs associated with the selected product and scale.
   *
   * @param {object} product - Selected product.
   * @param {string} scale -   - Selected customer scale.
   * @returns {string}       - Sentence describing daily charge costs.
   */
  // @ts-expect-error - Automatic, Please fix when editing this file
  getDailyChargeText: function getDailyChargeText(product, scale) {
    if (!product.coPayment) {
      return '';
    }

    const {
      coPayment: { limits, value },
    } = product;

    // Family cap information shows if the scale is NOT family
    const familyCapInformation =
      scale !== constants.SCALE.FAMILY
        ? ` and the family cap is $${limits[constants.SCALE.FAMILY]} each membership year`
        : '';
    return (
      `You’re on a $${value} daily charge per person as a ` +
      // @ts-expect-error [🤖 PLEASE FIX 🤖]: Element implicitly has an 'any' type because expression of type 'any' can't be used to index type '{ single: string; couple: string; family: string; "single-parent": string; }'.
      `${lowerCase(constants.SCALE_LABEL[scale])} with a cap of ` +
      `$${limits[scale]}${familyCapInformation}.`
    );
  },

  /**
   * Get a product, with caching.
   *
   * @param {Array} products - Products to pick from.
   * @param {string} productId - Product id to get.
   * @returns {object} Product that matches.
   */
  // @ts-expect-error - Automatic, Please fix when editing this file
  getProduct: (products, productId) => products?.[productId],

  getProducts: (products: Record<string, ProductType> | undefined, productIds: ProductType['id'][]) =>
    productIds.reduce<ProductType[]>((selectedProducts, productId) => {
      const product = products?.[productId];

      if (product) {
        selectedProducts.push(product);
      }

      return selectedProducts;
    }, []),

  isAvailableFor: (product: ProductType, scale: string) => includes(scale, product?.scales?.selectable),
  // TODO: [HER-3644] Cleanup `isAvailableIn` code as Ambulance isn't saleable on the site
  // @ts-expect-error - Automatic, Please fix when editing this file
  isAvailableIn: function isAvailableIn(product, state) {
    if (product.id === constants.PRODUCT.AMBULANCE) {
      return state === constants.STATE.NSW || state === constants.STATE.ACT;
    }

    return true;
  },

  /**
   * Load a product from the API.
   *
   * @param {object} api - The api to request the product from.
   * @param {string} productId - The product id to load.
   * @returns {promise} A promise that resolves when the product has loaded.
   */
  loadProduct: function loadProduct(api: ProductApi, productId: ProductType['id']) {
    const isProductIdValid = some((validProductId) => validProductId === productId, constants.PRODUCT);

    if (!isProductIdValid) {
      // eslint-disable-next-line prefer-promise-reject-errors
      return Promise.reject({
        message: `Invalid ProductId provided: '${productId}'`,
        type: 'ProductIdMissedError',
      });
    }

    const key = productId;

    if (!ProductService.promises[key]) {
      const productPromise: Promise<ProductType> = api
        .get(`/products/${productId}`)
        .then((json) => json.product)
        .finally(() => {
          if (ProductService.promises[key] === productPromise) {
            delete ProductService.promises[key];
          }
        });
      ProductService.promises[key] = productPromise;
    }

    return ProductService.promises[key];
  },

  loadProducts: function loadProducts(api: ProductApi, productIds: ProductType['id'][]) {
    const fn = partial(ProductService.loadProduct, [api]);

    const promise = Promise.all(map(fn, productIds));
    promise.catch(() => ProductService.reset());
    return promise;
  },
  promises: {} as Record<ProductType['id'], Promise<ProductType>>,
  reset: function reset() {
    ProductService.promises = {};
  },
};
export default ProductService;
